import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  FlaskConical,
  Clock,
  Database,
  Mail,
  MailWarning,
  Moon,
  MousePointerClick,
  PlayCircle,
  RefreshCw,
  Loader2,
  Send,
  Sun,
  Users,
  XCircle,
  Zap,
} from "lucide-react";
import {
  domainList,
  errorMessage,
  formatNumber,
  formatRate,
  formatRelative,
  hourLabel,
  type CommsOverview,
} from "@/lib/comms";
import { CardsSkeleton, EmptyState, ErrorState, StatCard } from "./shared";

const BOUNCE_LIMIT = 2;
const COMPLAINT_LIMIT = 0.1;

const PROVIDER_LABELS: Array<{ key: keyof CommsOverview["providerReady"]; label: string; hint: string }> = [
  { key: "resend", label: "Resend", hint: "RESEND_API_KEY is set." },
  { key: "ses", label: "Amazon SES", hint: "AWS credentials and region are set." },
  { key: "whatsapp", label: "WhatsApp", hint: "Twilio WhatsApp sender is configured." },
  { key: "sms", label: "SMS", hint: "Twilio SMS sender is configured." },
  { key: "resendWebhookSecret", label: "Resend webhook secret", hint: "Needed to trust bounce and complaint callbacks." },
];

function ProviderChip({ ready, label, hint }: { ready: boolean; label: string; hint: string }) {
  return (
    <div
      className={`flex items-start gap-2 rounded-md border px-3 py-2 ${ready ? "border-emerald-200 dark:border-emerald-900" : "border-amber-200 dark:border-amber-900"}`}
      title={hint}
      data-testid={`provider-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      {ready
        ? <CheckCircle2 className="w-4 h-4 mt-0.5 text-emerald-600 dark:text-emerald-400 shrink-0" />
        : <XCircle className="w-4 h-4 mt-0.5 text-amber-600 dark:text-amber-400 shrink-0" />}
      <div className="min-w-0">
        <p className="text-sm font-medium leading-tight">{label}</p>
        <p className="text-xs text-muted-foreground leading-snug">{ready ? "Ready" : "Not configured"}</p>
      </div>
    </div>
  );
}

export default function OverviewTab() {
  const { toast } = useToast();
  const { data, isLoading, error } = useQuery<CommsOverview>({ queryKey: ["/api/admin/comms/overview"] });

  const invalidateOverview = () => {
    queryClient.invalidateQueries({ queryKey: ["/api/admin/comms/overview"] });
  };

  const refreshMutation = useMutation({
    mutationFn: async () => (await apiRequest("POST", "/api/admin/comms/refresh")).json(),
    onSuccess: (res: { facts?: number; contacts?: { students?: number; parents?: number } }) => {
      invalidateOverview();
      queryClient.invalidateQueries({ queryKey: ["/api/admin/comms/contacts"] });
      toast({
        title: "Contacts and facts refreshed",
        description: `${formatNumber(res.facts)} facts rebuilt. ${formatNumber(res.contacts?.students)} student and ${formatNumber(res.contacts?.parents)} parent contacts synced.`,
      });
    },
    onError: (e: unknown) => toast({ title: "Refresh failed", description: errorMessage(e), variant: "destructive" }),
  });

  const tickMutation = useMutation({
    mutationFn: async () => (await apiRequest("POST", "/api/admin/comms/worker/tick")).json(),
    onSuccess: (res: { sent?: number; failed?: number; suppressed?: number; deferred?: boolean }) => {
      invalidateOverview();
      queryClient.invalidateQueries({ queryKey: ["/api/admin/comms/campaigns"] });
      toast({
        title: res.deferred ? "Queue deferred for quiet hours" : "Queue processed",
        description: res.deferred
          ? "Nothing was sent. The queue is held until quiet hours end."
          : `${formatNumber(res.sent)} sent, ${formatNumber(res.failed)} failed, ${formatNumber(res.suppressed)} suppressed.`,
      });
    },
    onError: (e: unknown) => toast({ title: "Could not process the queue", description: errorMessage(e), variant: "destructive" }),
  });

  const automationsMutation = useMutation({
    mutationFn: async () => (await apiRequest("POST", "/api/admin/comms/automations/run")).json(),
    onSuccess: (res: { queued?: number; skipped?: number }) => {
      invalidateOverview();
      queryClient.invalidateQueries({ queryKey: ["/api/admin/comms/automations"] });
      toast({
        title: "Automations evaluated",
        description: `${formatNumber(res.queued)} queued, ${formatNumber(res.skipped)} skipped.`,
      });
    },
    onError: (e: unknown) => toast({ title: "Could not run automations", description: errorMessage(e), variant: "destructive" }),
  });

  const busy = refreshMutation.isPending || tickMutation.isPending || automationsMutation.isPending;

  if (error) {
    return <Card><CardContent><ErrorState error={error} /></CardContent></Card>;
  }

  if (isLoading || !data) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-24" />
        <CardsSkeleton count={8} className="h-28" />
        <div className="grid gap-4 lg:grid-cols-2">
          <Skeleton className="h-64" />
          <Skeleton className="h-64" />
        </div>
      </div>
    );
  }

  const { health, contacts, queue, facts, providerReady, recentEvents } = data;
  const capToday = Math.max(0, Number(data.capToday || 0));
  const sentToday = Math.max(0, Number(health.email_sent_today || 0));
  const capPct = capToday > 0 ? Math.min(100, Math.round((sentToday / capToday) * 100)) : 0;
  const bounceHot = Number(health.bounceRate || 0) > BOUNCE_LIMIT;
  const complaintHot = Number(health.complaintRate || 0) > COMPLAINT_LIMIT;

  const sandbox = data.settings?.sandbox;

  return (
    <div className="space-y-6">
      {sandbox?.enabled && (
        <div
          className="flex items-start gap-3 rounded-md border border-amber-400 bg-amber-50 dark:border-amber-700 dark:bg-amber-950/40 p-4"
          data-testid="banner-sandbox"
        >
          <FlaskConical className="w-5 h-5 mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
          <div>
            <p className="font-semibold text-amber-900 dark:text-amber-200">
              Sandbox mode is on: only {domainList(sandbox.allowDomains)} receive messages
            </p>
            <p className="text-sm text-amber-800 dark:text-amber-300">
              Everything else is queued and recorded as sandboxed instead of being delivered, and WhatsApp and SMS are blocked entirely. Turn it off
              in Settings when you are ready to send for real.
            </p>
          </div>
        </div>
      )}

      {/* Maintenance */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
            <div>
              <CardTitle className="text-base">Maintenance</CardTitle>
              <CardDescription>
                Refresh rebuilds the per-child facts the segments read from and syncs the contact book against the user table. It can take a minute.
                Process the queue to send what is due right now, and run automations to evaluate every enabled trigger immediately.
              </CardDescription>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                onClick={() => refreshMutation.mutate()}
                disabled={busy}
                data-testid="button-refresh-facts"
              >
                {refreshMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                Refresh contacts and facts
              </Button>
              <Button
                variant="outline"
                onClick={() => tickMutation.mutate()}
                disabled={busy}
                data-testid="button-worker-tick"
              >
                {tickMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PlayCircle className="w-4 h-4 mr-2" />}
                Process queue now
              </Button>
              <Button
                variant="outline"
                onClick={() => automationsMutation.mutate()}
                disabled={busy}
                data-testid="button-run-automations"
              >
                {automationsMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
                Run automations now
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <Database className="w-3.5 h-3.5" />
            {formatNumber(facts.count)} facts, refreshed {formatRelative(facts.refreshedAt)}
          </span>
          <span className="flex items-center gap-1.5">
            {data.inQuietHours ? <Moon className="w-3.5 h-3.5" /> : <Sun className="w-3.5 h-3.5" />}
            {data.inQuietHours ? "In quiet hours" : "Outside quiet hours"}, Jamaica time is {hourLabel(data.jamaicaHour)}
          </span>
          <span className="flex items-center gap-1.5">
            <Send className="w-3.5 h-3.5" /> {formatNumber(data.activeCampaigns)} active campaign{data.activeCampaigns === 1 ? "" : "s"}
          </span>
          <span className="flex items-center gap-1.5">
            <Zap className="w-3.5 h-3.5" /> {formatNumber(data.automations.enabled)} of {formatNumber(data.automations.total)} automations enabled
          </span>
        </CardContent>
      </Card>

      {/* Deliverability, last 30 days */}
      <div>
        <h2 className="text-sm font-semibold mb-3">Deliverability, last 30 days</h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label="Emails sent" value={health.email_sent} icon={Mail} hint="Accepted by the provider in the last 30 days." />
          <StatCard
            label="Bounce rate"
            value={formatRate(health.bounceRate)}
            icon={MailWarning}
            tone={bounceHot ? "danger" : "default"}
            hint={bounceHot ? `Above the ${BOUNCE_LIMIT}% safe line. Clean the list before sending more.` : `Safe below ${BOUNCE_LIMIT}%. ${formatNumber(health.email_bounced)} bounced.`}
          />
          <StatCard
            label="Complaint rate"
            value={formatRate(health.complaintRate)}
            icon={AlertTriangle}
            tone={complaintHot ? "danger" : "default"}
            hint={complaintHot ? `Above the ${COMPLAINT_LIMIT}% safe line. Pause marketing until this drops.` : `Safe below ${COMPLAINT_LIMIT}%. ${formatNumber(health.email_complained)} complaints.`}
          />
          <StatCard label="Open rate" value={formatRate(health.openRate)} icon={Activity} hint={`${formatNumber(health.email_opened)} opens recorded.`} />
          <StatCard label="Click rate" value={formatRate(health.clickRate)} icon={MousePointerClick} hint={`${formatNumber(health.email_clicked)} clicks recorded.`} />
          <StatCard label="WhatsApp sent" value={health.whatsapp_sent} icon={Send} hint={`${formatNumber(health.sms_sent)} SMS sent in the same window.`} />
          <StatCard label="Failed" value={health.failed} icon={XCircle} tone={health.failed > 0 ? "danger" : "default"} hint="Sends the provider rejected outright." />
          <StatCard label="Queued" value={health.queued} icon={Clock} hint="Waiting in the send queue right now." />
        </div>
      </div>

      {/* Cap, queue and quiet hours */}
      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><Mail className="w-4 h-4" /> Today's sending cap</CardTitle>
            <CardDescription>
              The warm-up schedule raises this cap over time so the sending domain builds a reputation instead of being throttled.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-baseline justify-between">
              <p className="text-2xl font-bold" data-testid="text-cap-progress">
                {formatNumber(sentToday)} <span className="text-muted-foreground text-base font-normal">of {formatNumber(capToday)} emails</span>
              </p>
              <span className="text-sm text-muted-foreground">{capPct}% used</span>
            </div>
            <Progress value={capPct} className="h-2" />
            <p className="text-xs text-muted-foreground">
              {data.settings?.warmup?.enabled
                ? `Warm-up is on: it starts at ${formatNumber(data.settings.warmup.startPerDay)} a day and doubles every ${formatNumber(data.settings.warmup.doubleEveryDays)} days, capped by the daily limit of ${formatNumber(data.settings.dailyEmailCap)}.`
                : `Warm-up is off, so the cap is the flat daily limit of ${formatNumber(data.settings?.dailyEmailCap)}.`}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><Clock className="w-4 h-4" /> Queue</CardTitle>
            <CardDescription>What the worker will pick up next.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Due now</span><span className="font-semibold">{formatNumber(queue.due)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Scheduled later</span><span className="font-semibold">{formatNumber(queue.scheduled)}</span></div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Failed, last 7 days</span>
              <span className={`font-semibold ${queue.failed_7d > 0 ? "text-destructive" : ""}`}>{formatNumber(queue.failed_7d)}</span>
            </div>
            <div className="pt-2 border-t">
              <Badge
                className={`border-0 ${data.inQuietHours ? "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300" : "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"}`}
                data-testid="badge-quiet-hours"
              >
                {data.inQuietHours ? "Quiet hours, sending held" : "Sending window open"}
              </Badge>
              <p className="text-xs text-muted-foreground mt-1.5">
                Quiet hours run {hourLabel(data.settings?.quietHours?.start)} to {hourLabel(data.settings?.quietHours?.end)} Jamaica time.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Contact reach */}
      <div>
        <h2 className="text-sm font-semibold mb-3">Contact reach</h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
          <StatCard label="Reachable by email" value={contacts.email_reachable} icon={Mail} hint={`${formatNumber(contacts.total)} contacts on file.`} />
          <StatCard label="Reachable by WhatsApp" value={contacts.whatsapp_reachable} icon={Send} hint={`${formatNumber(contacts.parents)} parents, ${formatNumber(contacts.students)} students.`} />
          <StatCard label="Bounced" value={contacts.bounced} icon={MailWarning} tone={contacts.bounced > 0 ? "danger" : "default"} hint="Addresses the provider could not deliver to." />
          <StatCard label="Complained" value={contacts.complained} icon={AlertTriangle} tone={contacts.complained > 0 ? "danger" : "default"} hint="Marked our mail as spam." />
          <StatCard label="Unsubscribed" value={contacts.unsubscribed} icon={Users} hint="Opted out. Never included in a send." />
          <StatCard label="Suppressions" value={contacts.suppressions} icon={XCircle} hint="Blocked addresses across all channels." />
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* Providers */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Provider readiness</CardTitle>
            <CardDescription>A channel with a missing key is refused at launch, not silently dropped.</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-2 sm:grid-cols-2">
            {PROVIDER_LABELS.map(p => (
              <ProviderChip key={p.key} ready={!!providerReady?.[p.key]} label={p.label} hint={p.hint} />
            ))}
          </CardContent>
        </Card>

        {/* Recent events */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Recent events</CardTitle>
            <CardDescription>Opens, clicks, bounces and complaints as the providers report them.</CardDescription>
          </CardHeader>
          <CardContent>
            {!recentEvents || recentEvents.length === 0 ? (
              <EmptyState title="No events yet" hint="Events land here once a campaign has been sent and the provider webhooks are configured." />
            ) : (
              <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
                {recentEvents.map(ev => (
                  <div key={ev.id} className="flex items-start justify-between gap-3 text-sm border-b last:border-0 pb-2 last:pb-0">
                    <div className="min-w-0">
                      <span className="font-medium">{ev.type}</span>
                      <span className="text-muted-foreground"> · {ev.channel}{ev.provider ? ` · ${ev.provider}` : ""}</span>
                      {ev.url ? <p className="text-xs text-muted-foreground truncate" title={ev.url}>{ev.url}</p> : null}
                    </div>
                    <span className="text-xs text-muted-foreground whitespace-nowrap">{formatRelative(ev.occurredAt)}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
